import { useEffect, useState, useCallback } from "react";
import { FoodsTextInput, FoodsTextInputProps } from "@foodpilot/foods";
import { parseLocalizedNumber, formatNumberByType, isValidStandardNumber } from "src/utils/formatting";
import { Field } from "src/api/sure-api/ssq/useGetOneSsqVersion";

type LocalizedFoodsTextInputProps = FoodsTextInputProps & {
    /**
     * The value in standard format (with dot as decimal separator)
     */
    value: string | number;

    /**
     * Receives numbers in standard format, or the raw draft when parsing fails.
     */
    onChange: (value: string) => void;

    /**
     * The field type (e.g., "text", "number")
     */
    type?: Field["type"];

    /**
     * The unit title from SSQ property (e.g., "decimal", "integer", "%", "kg")
     */
    unitTitle?: string;

    /**
     * The locale for formatting (e.g., "fr", "en")
     */
    locale: string;

    /**
     * Optional maximum fraction digits override
     */
    maxFractionDigits?: number;

    /**
     * Enable number localization (default: true)
     */
    enableLocalization?: boolean;
};

/**
 * A FoodsTextInput wrapper that adds number localization support.
 * Formats numbers according to the user's locale while maintaining
 * a standard format (dot decimal) for data storage.
 *
 * This is a lighter alternative to SSQTextInput when you don't need
 * tooltips, scores, or other SSQ-specific features.
 */
export const LocalizedFoodsTextInput = (props: LocalizedFoodsTextInputProps) => {
    const {
        value,
        onChange,
        type,
        unitTitle,
        locale,
        maxFractionDigits,
        enableLocalization = true,
        ExtraInputProps = {},
        ...otherProps
    } = props;

    // State for the displayed (formatted) value
    const [displayValue, setDisplayValue] = useState<string>("");
    const [isFocused, setIsFocused] = useState(false);

    // Check if this should be treated as a numeric field
    const isNumeric = enableLocalization && type === "number";
    const hasValue = String(value).trim() !== "";
    const isInvalid = isNumeric && hasValue && !isValidStandardNumber(value);

    // Format the value for display when not focused
    useEffect(() => {
        if (!enableLocalization) {
            setDisplayValue(value === undefined || value === null ? "" : String(value));
            return;
        }

        if (!isFocused && value && isNumeric && !isInvalid) {
            const formatted = formatNumberByType(value, unitTitle, locale, maxFractionDigits);
            setDisplayValue(formatted);
        } else if (!isFocused && value && (!isNumeric || isInvalid)) {
            // For non-numeric fields, just show the value as-is
            setDisplayValue(String(value));
        } else if (!isFocused && !value) {
            setDisplayValue("");
        }
    }, [value, unitTitle, locale, maxFractionDigits, isFocused, isNumeric, isInvalid, enableLocalization]);

    const handleChange = useCallback(
        (newValue: string) => {
            setDisplayValue(newValue);

            if (!enableLocalization || !isNumeric) {
                // For non-numeric fields or when localization is disabled, pass through directly
                onChange(newValue);
                return;
            }

            // Parse the localized input to standard format
            const standardValue = parseLocalizedNumber(newValue, locale);

            if (newValue.trim() !== "" && standardValue === "") {
                onChange(newValue);

                return;
            }

            onChange(standardValue);
        },
        [locale, onChange, isNumeric, enableLocalization],
    );

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (isNumeric && value && !isInvalid) {
                setDisplayValue(formatNumberByType(value, unitTitle, locale, maxFractionDigits));
            }

            setIsFocused(true);
            ExtraInputProps.onFocus?.(e);
        },
        [ExtraInputProps, isNumeric, value, isInvalid, unitTitle, locale, maxFractionDigits],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocused(false);

            if (isInvalid) {
                return;
            }

            ExtraInputProps.onBlur?.(e);
        },
        [ExtraInputProps, isInvalid],
    );

    return (
        <FoodsTextInput
            {...otherProps}
            error={otherProps.error || isInvalid}
            value={displayValue}
            onChange={handleChange}
            ExtraInputProps={{
                ...ExtraInputProps,
                type: enableLocalization && isNumeric ? "text" : ExtraInputProps.type,
                onFocus: handleFocus,
                onBlur: handleBlur,
                inputProps: {
                    ...ExtraInputProps.inputProps,
                    // Same reason as SSQTextInput: only `inputProps` reaches the <input>.
                    inputMode: enableLocalization && isNumeric ? "decimal" : ExtraInputProps.inputMode,
                },
            }}
        />
    );
};
